import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSiteAssets } from '../services/firebaseBlogService';

const SiteAssetsContext = createContext();

export const SiteAssetsProvider = ({ children }) => {
  // Initialize state synchronously from localStorage cache to prevent any visual flash
  const [assets, setAssets] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_site_assets_formatted');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error('Failed to load initial site assets cache:', e);
    }
    return { urls: {}, raw: {} };
  });
  
  const [loading, setLoading] = useState(() => {
    return !localStorage.getItem('cached_site_assets_formatted');
  });

  const refreshAssets = async () => {
    try {
      const data = await getSiteAssets();
      // Format data: data has form { key: { url: '...', storagePath: '...' } }
      // We only map the asset key to its URL for easy use, e.g. assets.home_hero
      const formattedAssets = {};
      const fullAssetsMap = {};
      
      Object.keys(data).forEach(key => {
        formattedAssets[key] = data[key].url;
        fullAssetsMap[key] = data[key];
      });
      
      const nextAssets = {
        urls: formattedAssets,
        raw: fullAssetsMap
      };
      
      setAssets(nextAssets);
      
      // Save formatted assets to localStorage for instant synchronous loading on next render
      try {
        localStorage.setItem('cached_site_assets_formatted', JSON.stringify(nextAssets));
      } catch (e) {
        console.error('Failed to cache site assets:', e);
      }
    } catch (error) {
      console.error('Error loading site assets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAssets();
  }, []);

  return (
    <SiteAssetsContext.Provider value={{ 
      assets: assets.urls || {}, 
      rawAssets: assets.raw || {}, 
      loading, 
      refreshAssets 
    }}>
      {children}
    </SiteAssetsContext.Provider>
  );
};

export const useSiteAssets = () => {
  const context = useContext(SiteAssetsContext);
  if (!context) {
    throw new Error('useSiteAssets must be used within a SiteAssetsProvider');
  }
  return context;
};
